import React from 'react';

import { Box, HStack, Modal, ModalBody, ModalContent, ModalOverlay, Text, rem } from '@vimeo/bokeh';
import { Diamond } from '@vimeo/bokeh-icons';
import { useCuidEppoAssignment, VariationTypes } from '@vimeo/eppo';
import { translate } from '@vimeo/i18n-runtime';
import { shouldShowInDevelopmentFeature } from '@vimeo/query-feature-flag';
import { tokens } from '@vimeo/ui/tokens';
import { useViewer } from '@vimeo/viewer';

import { defaultTemplateProps, PlanData, UserData, TrackingProps } from '../../utils/interface';
import { isSeatTierPlan } from '../../utils/utils';
import Footer from './Footer';
import Header from './Header';
import PlanCards from './PlanCards';

interface Props {
  userData: UserData | null;
  plansData: PlanData[] | null;
  modalConfig?: defaultTemplateProps;
  onClose: VoidFunction;
  tracking: TrackingProps;
}

const DefaultModal = (props: Props) => {
  const viewer = useViewer();
  const { assignment: storageQuotaModalAssignment } = useCuidEppoAssignment({
    flagKey: 'storage-quota-modal',
    assignmentType: VariationTypes.STRING,
    defaultAssignment: 'control',
    disabled: !viewer?.user,
  });
  const showStorageQuotaModalUpdates =
    shouldShowInDevelopmentFeature('blue_highlight') || storageQuotaModalAssignment === 'variant';

  const { modalConfig, plansData } = props;

  // Show a different
  const modalConfigBannerText = modalConfig?.featuredBannerText;
  const featuredBannerText =
    plansData?.length && isSeatTierPlan(plansData[0].tier)
      ? modalConfigBannerText ?? translate('This feature is included in all paid plans')
      : translate('Upgrade to unlock more features');

  if (modalConfig)
    modalConfig.hasFreeFeaturedBanner =
      modalConfig?.hasFreeFeaturedBanner || modalConfigBannerText !== undefined;

  const hasFreeFeaturedBanner = modalConfig?.hasFreeFeaturedBanner;

  return (
    <Modal
      data-testid="default-upsell-container"
      isOpen={true}
      onClose={props.onClose}
      scrollBehavior="outside"
    >
      <ModalOverlay />
      <ModalContent
        top={{ base: 'initial', sm: 'sm', md: 'lg', xl: 'initial' }}
        marginTop={hasFreeFeaturedBanner ? rem(40) : undefined}
        alignSelf={{ base: 'flex-start', xl: 'center' }}
        maxWidth={{ sm: rem(375), md: rem(618), lg: rem(618), xl: rem(1180) }}
      >
        <ModalBody padding={{ base: 'md', xl: 'lg' }}>
          {hasFreeFeaturedBanner && (
            <Box
              borderRadius="xl"
              position="absolute"
              width="100%"
              top={rem(-40)}
              left={0}
              verticalAlign="middle"
              justifyItems="center"
              pt="sm"
              pb="sm"
              pr="md"
              pl="md"
              backgroundColor="upsell-secondary"
              height={rem(80)}
              zIndex={-1}
            >
              <HStack>
                <Diamond
                  h={tokens.space.page.sm}
                  w={tokens.space.page.sm}
                  color="white"
                  backgroundColor="upsell-primary"
                  borderRadius={rem(6)}
                  padding={rem(4)}
                />
                <Text variant="body-lg" color="text-primary">
                  {featuredBannerText}
                </Text>
              </HStack>
            </Box>
          )}
          <Header
            modalConfig={modalConfig}
            onClose={props.onClose}
            userName={props.userData?.name}
          />
          {props.userData && plansData && (
            <PlanCards
              modalConfig={modalConfig}
              userData={props.userData}
              plansData={plansData}
              showStorageQuotaModalUpdates={showStorageQuotaModalUpdates}
            />
          )}
          <Footer />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default DefaultModal;
